import { useEffect } from "react";

/**
 * Finger-drag scrolling for a scroll container.
 *
 * The kiosks (ProDVX panels) only scrolled through the scrollbar — dragging a
 * finger over the content did nothing, while the very same build scrolls fine
 * in a desktop browser with touch emulation. The page's own CSS is correct
 * (`overflow-y: auto`, `-webkit-overflow-scrolling: touch`, overlays are
 * `pointer-events: none`, framer-motion sets no `touch-action` without `drag`),
 * so the difference lives in the device's runtime, which we do not control and
 * which changes with every panel the client buys.
 *
 * So instead of guessing at WebView versions, we drive the scroll ourselves
 * from pointer events. `touch-action: none` hands the gesture over completely,
 * which also means the behaviour is now IDENTICAL on the kiosk and on a
 * developer's machine — a reported scroll problem can finally be reproduced
 * with a mouse.
 *
 * Taps are preserved: nothing happens until the finger travels past
 * `DRAG_THRESHOLD_PX`, and a drag that crossed it swallows the click it would
 * otherwise produce, so sliding across a button never dials reception.
 */

/** Movement before a touch counts as a drag instead of a tap. */
const DRAG_THRESHOLD_PX = 8;

/** Velocity decay per frame after release. Lower = stops sooner. */
const MOMENTUM_FRICTION = 0.94;

/** Below this (px/ms) the glide is over. */
const MOMENTUM_MIN_VELOCITY = 0.02;

/**
 * Only the most recent movement should decide the fling, otherwise a slow drag
 * ending in a flick (or the reverse) throws the list the wrong way.
 */
const VELOCITY_SMOOTHING = 0.7;

export const useDragScroll = (
  element: HTMLElement | null,
  enabled = true,
): void => {
  useEffect(() => {
    if (!element || !enabled) {
      return undefined;
    }

    // Local handle for the whole effect: the element this run subscribed to is
    // the one it must also scroll and clean up, even if the prop changes.
    const node = element;

    // Touches stand in for pointers where the runtime has no Pointer Events.
    const hasPointerEvents = typeof window.PointerEvent === "function";

    let activePointerId: number | null = null;
    let isDragging = false;
    let startY = 0;
    let lastY = 0;
    let lastMoveAt = 0;
    let velocity = 0;
    let momentumFrame: number | null = null;

    /**
     * A drag ends with a `click` the browser still considers legitimate. This
     * flag makes the capture-phase listener below eat exactly one of them.
     */
    let swallowNextClick = false;

    const stopMomentum = () => {
      if (momentumFrame !== null) {
        cancelAnimationFrame(momentumFrame);
        momentumFrame = null;
      }
    };

    const startMomentum = () => {
      let previousFrameAt = performance.now();

      const step = (now: number) => {
        const elapsed = now - previousFrameAt;
        previousFrameAt = now;

        const before = node.scrollTop;

        node.scrollTop -= velocity * elapsed;

        velocity *= MOMENTUM_FRICTION;

        // Hitting the top or the bottom ends the glide — without this the loop
        // keeps running against a wall until friction alone stops it.
        const hitBounds = node.scrollTop === before;

        if (hitBounds || Math.abs(velocity) < MOMENTUM_MIN_VELOCITY) {
          momentumFrame = null;
          return;
        }

        momentumFrame = requestAnimationFrame(step);
      };

      momentumFrame = requestAnimationFrame(step);
    };

    const onPointerDown = (event: PointerEvent) => {
      // Right/middle mouse buttons must not grab the list.
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }

      stopMomentum();

      activePointerId = event.pointerId;
      isDragging = false;
      swallowNextClick = false;
      startY = event.clientY;
      lastY = event.clientY;
      lastMoveAt = event.timeStamp;
      velocity = 0;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (activePointerId !== event.pointerId) {
        return;
      }

      if (!isDragging) {
        if (Math.abs(event.clientY - startY) <= DRAG_THRESHOLD_PX) {
          return;
        }

        isDragging = true;

        // Keep receiving events once the finger slides off the container —
        // without capture a fast drag simply stops halfway.
        if (hasPointerEvents) {
          node.setPointerCapture(event.pointerId);
        }
      }

      const deltaY = event.clientY - lastY;
      const elapsed = event.timeStamp - lastMoveAt;

      node.scrollTop -= deltaY;

      if (elapsed > 0) {
        const instant = deltaY / elapsed;

        velocity =
          velocity * (1 - VELOCITY_SMOOTHING) + instant * VELOCITY_SMOOTHING;
      }

      lastY = event.clientY;
      lastMoveAt = event.timeStamp;
    };

    const endDrag = (event: PointerEvent) => {
      if (activePointerId !== event.pointerId) {
        return;
      }

      if (hasPointerEvents && node.hasPointerCapture(event.pointerId)) {
        node.releasePointerCapture(event.pointerId);
      }

      if (isDragging) {
        swallowNextClick = true;

        if (Math.abs(velocity) >= MOMENTUM_MIN_VELOCITY) {
          startMomentum();
        }
      }

      activePointerId = null;
      isDragging = false;
    };

    const onClickCapture = (event: MouseEvent) => {
      if (!swallowNextClick) {
        return;
      }

      swallowNextClick = false;

      event.preventDefault();
      event.stopPropagation();
    };

    const previousTouchAction = node.style.touchAction;

    // We own the gesture now; leaving the native one on would scroll twice on
    // the devices where it does work.
    node.style.touchAction = "none";

    node.addEventListener("click", onClickCapture, true);

    if (hasPointerEvents) {
      node.addEventListener("pointerdown", onPointerDown);
      node.addEventListener("pointermove", onPointerMove);
      node.addEventListener("pointerup", endDrag);
      node.addEventListener("pointercancel", endDrag);

      return () => {
        stopMomentum();

        node.style.touchAction = previousTouchAction;

        node.removeEventListener("pointerdown", onPointerDown);
        node.removeEventListener("pointermove", onPointerMove);
        node.removeEventListener("pointerup", endDrag);
        node.removeEventListener("pointercancel", endDrag);
        node.removeEventListener("click", onClickCapture, true);
      };
    }

    // Pre-Chrome-55 WebViews: feed the same handlers from touches.
    const asPointer = (event: TouchEvent) =>
      ({
        pointerId: 1,
        pointerType: "touch",
        button: 0,
        clientY: event.changedTouches[0]?.clientY ?? lastY,
        timeStamp: event.timeStamp,
      } as unknown as PointerEvent);

    const onTouchStart = (event: TouchEvent) => onPointerDown(asPointer(event));

    const onTouchMove = (event: TouchEvent) => {
      onPointerMove(asPointer(event));

      if (isDragging) {
        event.preventDefault();
      }
    };

    const onTouchEnd = (event: TouchEvent) => endDrag(asPointer(event));

    node.addEventListener("touchstart", onTouchStart, { passive: true });
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    node.addEventListener("touchend", onTouchEnd);
    node.addEventListener("touchcancel", onTouchEnd);

    return () => {
      stopMomentum();

      node.style.touchAction = previousTouchAction;

      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
      node.removeEventListener("click", onClickCapture, true);
    };
  }, [element, enabled]);
};

export default useDragScroll;
